import React, {useCallback, useMemo, useState} from 'react';
import {useTheme} from '@mui/material';
import {assert} from 'assert-ts';
import type {PersonNameVariantFieldLayout} from 'components/fields/types';
import type {DataValue, NameVariantValue} from 'schemaDefinition/types';
import {useLocalization} from 'localization';
import {assertSimpleRequired} from 'schemaDefinition/functions';
import {valueAsNameVariantArray} from 'services/utils';
import {
  CheckboxField,
  Layout,
  SingleValueListField,
  useListOperations,
} from 'components';
import {useUniqueFieldId} from 'components/fields/hooks/useUniqueFieldId';
import type {DataFormNameVariantListProps} from './types';
import {useDataFormContext} from '../contexts';
import {compareNameVariants} from '../functions/compareNameVariants';
import {
  useAddItemLabel,
  usePersonNameVariantLabelsAndPlaceholders,
  useResolvedDataFormPartConfiguration,
} from '../hooks';
import {PersonNameVariant} from './fields';

const nameVariantFieldLayout: PersonNameVariantFieldLayout = {
  name: {
    layout: {
      flex: 3,
    },
  },
  surName: {
    layout: {
      flex: 3,
    },
  },
  regnalNumber: {
    layout: {
      flex: 1,
    },
  },
  addition: {
    layout: {
      flex: 3,
    },
  },
  mainForm: {
    layout: {
      width: '80px',
    },
  },
};

const hasAnySuffix = (value: DataValue): boolean => {
  const nameVariants = valueAsNameVariantArray(value);
  if (!nameVariants) return false;
  return nameVariants.some(v => v.addition);
};

const hasAnyNumber = (value: DataValue): boolean => {
  const nameVariants = valueAsNameVariantArray(value);
  if (!nameVariants) return false;
  return nameVariants.some(v => v.regnalNumber);
};

const newVariant = (
  withRegnalNumber: boolean,
  withSuffix: boolean,
): NameVariantValue => {
  const value: NameVariantValue = {};
  if (withRegnalNumber) {
    value.regnalNumber = '';
  }
  if (withSuffix) {
    value.addition = '';
  }
  return value;
};

export const DataFormPersonNameVariantList: React.FC<
  DataFormNameVariantListProps
> = ({part, useValue, valuePath, mode}) => {
  const {colors} = useTheme();
  const {t} = useLocalization();
  const {id, setEditValue, useOriginalValue} = useDataFormContext();

  const originalNameVariants = (useOriginalValue ?? (() => undefined))(
    valuePath,
    id,
    part.name,
  );

  /**
   * true: Match between Ebba and NORAF
   * false: Not match
   * undefined: if NORAF was not loaded/found
   */
  const isMainFormInNoraf = useCallback(
    (nameVariant: NameVariantValue): boolean | undefined => {
      if (
        !originalNameVariants ||
        !Array.isArray(originalNameVariants) ||
        !originalNameVariants.length
      ) {
        return undefined;
      }

      return compareNameVariants(
        originalNameVariants[0] as NameVariantValue,
        nameVariant,
      );
    },
    [originalNameVariants],
  );

  const {showWhenReadonlyAndEmpty, hideStructureOperations} =
    useResolvedDataFormPartConfiguration(part);
  const value = (useValue(valuePath, id, part.name) ?? null) as DataValue;
  const [showSuffix, setShowSuffix] = useState(() => hasAnySuffix(value));
  const [showRegnalNumber, setShowRegnalNumber] = useState(() =>
    hasAnyNumber(value),
  );

  const setItems = useCallback(
    (items: NameVariantValue[] | null) => setEditValue(valuePath, items, part),
    [part, setEditValue, valuePath],
  );

  assert(
    value === null || Array.isArray(value),
    'DataForm: "name variants"-value property must be array (or null)',
    {part: part, contextPath: valuePath, dataScope: value},
  );

  const requiredValue = useMemo(
    () =>
      !value || value.length === 0
        ? [newVariant(showRegnalNumber, showSuffix)]
        : value,
    [value, showRegnalNumber, showSuffix],
  );

  const {onAppendValue, onDeleteValue, onMoveValue} =
    useListOperations<NameVariantValue>(
      update => {
        const nextValue = update(requiredValue as NameVariantValue[]);
        setItems(nextValue);
        return nextValue;
      },
      () => newVariant(showRegnalNumber, showSuffix),
    );

  const {contentLabels, fieldLayoutWithPlaceholders} =
    usePersonNameVariantLabelsAndPlaceholders(
      showRegnalNumber,
      showSuffix,
      nameVariantFieldLayout,
    );
  const addLabel = useAddItemLabel(part);

  const numberFieldId = useUniqueFieldId(`${valuePath}.number`);
  const additionFieldId = useUniqueFieldId(`${valuePath}.addition`);

  const renderCustomActions = useCallback(() => {
    const handleChangeShowSuffix = (checked: boolean) => {
      const nameVariants = valueAsNameVariantArray(requiredValue);
      setShowSuffix(checked);
      const newValue = checked
        ? nameVariants?.map<NameVariantValue>(nv => ({
            ...nv,
            addition: nv.addition ?? '',
          }))
        : nameVariants?.map<NameVariantValue>(({addition: _, ...nv}) => nv);
      setItems(newValue ?? null);
    };

    const handleChangeShowNumber = (checked: boolean) => {
      const nameVariants = valueAsNameVariantArray(requiredValue);
      setShowRegnalNumber(checked);
      const newValue = checked
        ? nameVariants?.map<NameVariantValue>(nv => ({
            ...nv,
            regnalNumber: nv.regnalNumber ?? '',
          }))
        : nameVariants?.map<NameVariantValue>(({regnalNumber: _, ...nv}) => nv);
      setItems(newValue ?? null);
    };

    return (
      <Layout horizontal>
        <CheckboxField
          fieldId={numberFieldId}
          name={`${valuePath}.number`}
          value={showRegnalNumber}
          boxLabel={t('page.agents.nameVariants.regnalNumber')}
          onChange={handleChangeShowNumber}
        />
        <CheckboxField
          fieldId={additionFieldId}
          name={`${valuePath}.addition`}
          value={showSuffix}
          boxLabel={t('page.agents.nameVariants.addition')}
          onChange={handleChangeShowSuffix}
        />
      </Layout>
    );
  }, [
    numberFieldId,
    valuePath,
    showRegnalNumber,
    t,
    additionFieldId,
    showSuffix,
    requiredValue,
    setItems,
  ]);

  return (
    <SingleValueListField<NameVariantValue>
      addLabel={addLabel}
      label={'.'}
      contentLabels={contentLabels}
      itemBackgroundColor={colors.enhancedLightBeige}
      itemPadding={1}
      required={assertSimpleRequired(part.required)}
      readonly={mode === 'read-only'}
      showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
      values={requiredValue as NameVariantValue[]}
      getValueKey={idx => `${valuePath}[${idx}]`}
      renderValue={(idx, value, focusableId) => {
        return (
          <PersonNameVariant
            focusableId={focusableId}
            part={part}
            value={value as NameVariantValue}
            valuePath={`${valuePath}[${idx}]`}
            setFieldValue={setEditValue}
            isMainForm={idx === 0}
            isMainFormInNoraf={isMainFormInNoraf(value as NameVariantValue)}
            mode={mode}
            showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
            layout={fieldLayoutWithPlaceholders}
          />
        );
      }}
      renderCustomActions={renderCustomActions}
      onAppendValue={
        hideStructureOperations?.includes('add') ? undefined : onAppendValue
      }
      onDeleteValue={
        hideStructureOperations?.includes('delete') ? undefined : onDeleteValue
      }
      onMoveValue={
        hideStructureOperations?.includes('move') ? undefined : onMoveValue
      }
    />
  );
};
